<script lang="ts" setup>
import { AlertCircle, CheckCircle, XCircle } from 'lucide-vue-next';
import { computed, onMounted, ref } from 'vue';

import { ApiClient } from '@/services/ApiClient';
import StripePaymentForm from '@/stripe/StripePaymentForm.vue';
import { LanguageContext } from '@nzyme/i18n/LanguageContext.js';
import { createExponentialBackoff } from '@nzyme/utils/createExponentialBackoff.js';
import { Form } from '@nzyme/vue-forms/components/Form.jsx';
import { useTranslate } from '@nzyme/vue-i18n/useTranslate.js';
import { useLogger } from '@nzyme/vue-ioc/useLogger.js';
import { useService } from '@nzyme/vue-ioc/useService.js';
import { useDataSource } from '@nzyme/vue-utils/useDataSource.js';
import { Reveal } from '@nzyme/vue/components/Reveal.jsx';
import { ActorClient } from '@sigma/actors-client/ActorClient.js';
import { ChatActor } from '@sigma/api-core/actors/ChatActor.js';
import type { ChatSubscriptionRequest } from '@sigma/chat-common/ChatEvent.js';
import { getIsLastEvent } from '@sigma/chat-common/utils/getIsLastEvent.js';
import { defineChatEventProps } from '@sigma/client/chat/defineChatEventProps.js';
import { formatProductPrice } from '@sigma/common/product/formatProductPrice.js';
import { SubscriptionGetClientSecretEndpoint } from '@sigma/patient-api/subscription/SubscriptionGetClientSecretEndpoint.js';
import { SubscriptionGetDetailsEndpoint } from '@sigma/patient-api/subscription/SubscriptionGetDetailsEndpoint.js';
import { SubscriptionGetStatusEndpoint } from '@sigma/patient-api/subscription/SubscriptionGetStatusEndpoint.js';
import Button from '@sigma/ui/Button.vue';
import ChatEventWrapper from '@sigma/ui/chat/ChatEventWrapper.vue';
import InfoBox from '@sigma/ui/InfoBox.vue';
import Loader from '@sigma/ui/Loader.vue';
import { useMounted } from '@vueuse/core';

import chatCss from './chat.module.scss';
import * as l from './ChatSubscriptionRequest.loc.js';

type SubscriptionState = 'canceled' | 'new' | 'pending' | 'success';

const props = defineProps(defineChatEventProps<ChatSubscriptionRequest>());

const t = useTranslate();
const apiClient = useService(ApiClient);
const actorClient = useService(ActorClient);
const langCtx = useService(LanguageContext);
const logger = useLogger('ChatSubscriptionRequest');
const chatActorRef = actorClient.actor(ChatActor);

const clientSecret = ref<string | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);
const waitingForConfirmation = ref(false);
const stripeForm = ref<InstanceType<typeof StripePaymentForm>>();

const mounted = useMounted();

// Determine the subscription state solely based on the event stream.
// A SUBSCRIPTION_START event after this event means the subscription was activated successfully.
const subscriptionState = computed<SubscriptionState>(() => {
  const hasSubscriptionStarted = props.chat.events.some(
    e => e.seq > props.event.seq && e.type === 'SUBSCRIPTION_START' && e.subscriptionId === props.event.subscriptionId,
  );

  if (hasSubscriptionStarted) {
    return 'success';
  }

  if (waitingForConfirmation.value) {
    return 'pending';
  }

  const isLastEvent = getIsLastEvent(props.chat.events, props.event);

  if (isLastEvent) {
    return 'new';
  }

  return 'canceled';
});

// Load subscription details for display in the success state.
// Using lazy behavior so the API call only happens when the value is accessed in the template,
// which only occurs in the success state.
const subscriptionDetails = useDataSource({
  params: () => props.event.subscriptionId,
  load: subscriptionId => apiClient(SubscriptionGetDetailsEndpoint, { subscriptionId }),
  behavior: 'lazy',
});

const formattedPrice = computed(() => {
  const d = subscriptionDetails.value;
  if (!d) {
    return '';
  }

  return formatProductPrice({
    price: d.price,
    currency: d.currency,
    type: d.productType,
    lang: langCtx(),
  });
});

onMounted(async () => {
  // If the subscription state is already determined (success or canceled), nothing to load
  if (subscriptionState.value === 'success' || subscriptionState.value === 'canceled') {
    loading.value = false;
    return;
  }

  try {
    // First check if subscription is already active (handles reload after payment)
    const { status } = await apiClient(SubscriptionGetStatusEndpoint, {
      subscriptionId: props.event.subscriptionId,
    });

    if (status === 'ACTIVE') {
      // Subscription is active but agent hasn't processed step completion yet.
      // Ping the chat actor to re-evaluate and wait for the step to complete.
      waitingForConfirmation.value = true;
      loading.value = false;

      if (props.chat.chatId) {
        await chatActorRef.requests.pingAndWait(props.chat.chatId);
      }
      // Once the ping triggers SUBSCRIPTION_START event, hasSubscriptionStarted will
      // become true and subscriptionState will switch to 'success' reactively.
      return;
    }

    const result = await apiClient(SubscriptionGetClientSecretEndpoint, {
      subscriptionId: props.event.subscriptionId,
    });
    clientSecret.value = result.clientSecret;
  } catch {
    error.value = t(l.loadError);
  } finally {
    loading.value = false;
  }
});

async function submitPayment() {
  if (!stripeForm.value) {
    return;
  }

  error.value = null;
  await stripeForm.value.submit();
}

async function onPaymentSuccess() {
  waitingForConfirmation.value = true;
  error.value = null;

  // Poll subscription status with exponential backoff
  const backoff = createExponentialBackoff({
    baseDelay: 1000,
    maxRetries: 15,
    maxDelay: 5000,
  });

  while (mounted.value && backoff.canRetry()) {
    try {
      const { status } = await apiClient(SubscriptionGetStatusEndpoint, {
        subscriptionId: props.event.subscriptionId,
      });

      if (status === 'ACTIVE') {
        if (!mounted.value) {
          return;
        }

        waitingForConfirmation.value = false;

        // Ping the chat actor so it re-evaluates the subscription status
        if (props.chat.chatId) {
          await chatActorRef.requests.pingAndWait(props.chat.chatId);
        }
        return;
      }
    } catch (err) {
      logger.error('Failed to poll subscription status', {
        subscriptionId: props.event.subscriptionId,
        error: err,
      });
    }

    if (!mounted.value) {
      return;
    }

    await backoff.backoff();
  }

  if (!mounted.value) {
    return;
  }

  // Exhausted retries
  waitingForConfirmation.value = false;
  error.value = t(l.paymentError);
}

function onPaymentError(message: string | null) {
  error.value = message;
}
</script>

<template>
  <ChatEventWrapper
    :event="event"
    :class="[css.event, chatCss.container]"
  >
    <Reveal>
      <!-- Loading state -->
      <Loader
        v-if="loading"
        :class="css.loader"
      />

      <!-- Success state: subscription activated -->
      <InfoBox
        v-else-if="subscriptionState === 'success'"
        color="green"
        :icon="CheckCircle"
      >
        <div :class="css.subscriptionInfo">
          <strong>{{ t(l.subscriptionActivated) }}</strong>
          <template v-if="subscriptionDetails.value">
            <span>{{ t(l.subscriptionName, { name: subscriptionDetails.value.productName }) }}</span>
            <span>{{ t(l.subscriptionPrice, { price: formattedPrice }) }}</span>
          </template>
        </div>
      </InfoBox>

      <!-- Canceled state: payment was not completed -->
      <InfoBox
        v-else-if="subscriptionState === 'canceled'"
        :icon="XCircle"
      >
        {{ t(l.subscriptionCanceled) }}
      </InfoBox>

      <!-- Waiting for Stripe webhook confirmation -->
      <template v-else-if="subscriptionState === 'pending'">
        <Loader :class="css.loader" />
        <p :class="css.waitingText">{{ t(l.waitingForConfirmation) }}</p>
      </template>

      <!-- Payment form (new subscription state) -->
      <Form
        v-else-if="clientSecret && subscriptionState === 'new'"
        :class="css.form"
        @submit="submitPayment"
      >
        <StripePaymentForm
          ref="stripeForm"
          :client-secret="clientSecret"
          @success="onPaymentSuccess()"
          @error="onPaymentError($event)"
        />

        <InfoBox
          v-if="error"
          color="red"
          :icon="AlertCircle"
        >
          {{ error }}
        </InfoBox>

        <Button
          variant="primary"
          color="black"
          size="large"
          rounded
          type="submit"
        >
          {{ t(l.payButton) }}
        </Button>
      </Form>

      <!-- Error state (no client secret) -->
      <InfoBox
        v-else-if="error"
        color="red"
        :icon="AlertCircle"
      >
        {{ error }}
      </InfoBox>
    </Reveal>
  </ChatEventWrapper>
</template>

<style lang="scss" module="css">
.event {
  display: flex;
  flex-direction: column;
  padding-block: $spacing-16;
  gap: $spacing-16;
}

.form {
  display: flex;
  flex-direction: column;
  gap: $spacing-12;
}

.loader {
  width: 24px;
  height: 24px;
  margin-inline: auto;
  color: $color-blue-600;
}

.subscriptionInfo {
  display: flex;
  flex-direction: column;
  gap: $spacing-04;
}

.waitingText {
  text-align: center;
  font-size: $font-size-14;
  color: $color-gray-600;
  margin: 0;
}
</style>
